import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import hero from "../../assets/hero6.png";
import { FaCode, FaTools, FaArrowRight } from "react-icons/fa";
import { GiTrophy, GiMountainClimbing, GiGingerbreadMan } from "react-icons/gi";
import { PiFlowArrowBold } from "react-icons/pi";
import { MdQueryStats } from "react-icons/md";
// import EmbeddedForm from "./EmbeddedForm";

import Editor from "@monaco-editor/react";
//import AdventNotice from "./AdventNotice";
// import FeaturedLinks from "./Featured";

const HomePage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Create and append the script
    const script = document.createElement("script");
    script.src = "https://widget.senja.io/js/iframeResizer.min.js";
    script.async = true;

    script.onload = () => {
      // Once the script is loaded, initialize the iFrame resizer
      if (window.iFrameResize) {
        window.iFrameResize(
          { log: false, checkOrigin: false },
          "#wall-of-love-2yNE8N"
        );
      }
    };

    document.body.appendChild(script);

    // Cleanup function
    return () => {
      document.body.removeChild(script);
      // Remove any iframe resizer instances if they exist
      const iframe = document.getElementById("wall-of-love-2yNE8N");
      if (iframe && iframe.iFrameResizer) {
        iframe.iFrameResizer.removeListeners();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-zinc-900 text-white">
      <Helmet>
        <title>
          Learn Salesforce Development | Interactive Apex Coding Practice for
          Beginners
        </title>
        <meta
          name="description"
          content="Start learning Salesforce development from scratch with hands-on Apex coding practice. Interactive lessons, beginner-friendly challenges, and real-world projects. Perfect for aspiring Salesforce developers."
        />
        <meta
          name="keywords"
          content="learn apex coding, salesforce development tutorial, salesforce for beginners, apex programming practice, salesforce developer training, learn salesforce development, hands on apex, interactive apex lessons"
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Learn Salesforce Development | Interactive Apex Coding Practice"
        />
        <meta
          property="og:description"
          content="Get hands-on experience with Salesforce development through free interactive lessons and challenges. Perfect for beginners!"
        />
        <meta property="og:url" content="https://www.campapex.org" />
        <link rel="canonical" href="https://www.campapex.org/" />
      </Helmet>

      <Hero />
      {/* Hidden but kept for analytics
      <EmbeddedForm
        formId="670935d377"
        subheading="Stay up to date with latest courses, updates, and announcements"
      />
      */}
      <FeatureShowcase />
      <InteractiveDemo />

      {/* <FeedbackAnimation />
      <div className="p-4 sm:p-6 my-8 sm:my-12 text-center border border-white mx-4 sm:mx-auto max-w-4xl">
        <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-4 text-primary">
          This must cost a fortune!
        </h3>
        <p className="text-sm sm:text-base text-gray-300 mb-4 px-4 sm:px-8 md:px-16 lg:px-32">
          Nope! Where you live or your financial situation shouldn't stop you
          from learning Salesforce Development. Camp Apex is 100% free. You can{" "}
          <a
            href="https://buymeacoffee.com/campapexdotorg"
            className="text-yellow-400 hover:underline font-semibold"
            target="_blank"
            rel="noopener noreferrer"
          >
            donate
          </a>{" "}
          to help cover server costs if you'd like.
        </p>
      </div>*/}
      <Testimonials />
      {/* <FeaturedLinks /> */}
      <CallToAction navigate={navigate} />
      {/* <AdventNotice /> */}
    </div>
  );
};

const Hero = () => (
  <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 pt-16 pb-8 md:py-0">
    <div className="absolute inset-0 z-0">
      <ParticleBackground />
    </div>

    <div className="z-10 text-center max-w-6xl mx-auto px-4">
      <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-8 animate-fade-down leading-relaxed md:leading-tight">
        Interactive Lessons
        <br />
        to Learn <span className="text-[rgb(147,197,253)]">Salesforce</span> Development
      </h1>

      <p className="text-lg md:text-xl text-gray-400 mb-12 animate-fade-up delay-200 max-w-2xl mx-auto leading-relaxed">
        Learn by doing.
        <br className="hidden md:block" />
        Aspiring Developers Welcome.
      </p>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-scale delay-400">
        <Link
          to="/auth"
          className="group w-full sm:w-auto px-8 py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl
            transition-all duration-300 hover:shadow-xl hover:shadow-primary/25 hover:-translate-y-0.5"
        >
          <span className="flex items-center justify-center gap-2 text-base md:text-lg">
            Start Learning
            <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>
        <Link
          to="/courses"
          className="w-full sm:w-auto px-8 py-4 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded-xl
            border border-zinc-700 hover:border-zinc-600 transition-all duration-300"
        >
          <span className="text-base md:text-lg">Browse Courses</span>
        </Link>
      </div>
    </div>
  </section>
);

const ParticleBackground = () => {
  useEffect(() => {
    const canvas = document.getElementById("particle-canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const stars = [];
    const starCount = 150;

    for (let i = 0; i < starCount; i++) {
      const isBright = Math.random() < 0.1; // 10% chance of being a bright star
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: isBright ? Math.random() * 2 + 1.5 : Math.random() * 1.2 + 0.3,
        dx: (Math.random() - 0.5) * 0.1, // slower movement
        dy: (Math.random() - 0.5) * 0.1,
        baseOpacity: isBright ? 0.8 + Math.random() * 0.2 : 0.3 + Math.random() * 0.4,
        twinkleSpeed: 0.02 + Math.random() * 0.03,
        twinkleOffset: Math.random() * Math.PI * 2,
        isBright,
      });
    }

    let time = 0;

    function animate() {
      requestAnimationFrame(animate);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 1;

      for (let i = 0; i < starCount; i++) {
        const star = stars[i];

        // Twinkle effect
        const twinkle = Math.sin(time * star.twinkleSpeed + star.twinkleOffset);
        const opacity = star.baseOpacity + twinkle * 0.2;

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2, false);

        // Bright stars get a subtle blue-white color
        if (star.isBright) {
          ctx.fillStyle = `rgba(200, 220, 255, ${opacity})`;
          // Add a subtle glow for bright stars
          ctx.shadowBlur = 8;
          ctx.shadowColor = "rgba(150, 180, 255, 0.5)";
        } else {
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
          ctx.shadowBlur = 0;
        }
        ctx.fill();
        ctx.shadowBlur = 0; // Reset shadow

        // Very slow drift
        star.x += star.dx;
        star.y += star.dy;

        if (star.x < 0 || star.x > canvas.width) star.dx = -star.dx;
        if (star.y < 0 || star.y > canvas.height) star.dy = -star.dy;
      }
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return <canvas id="particle-canvas" className="absolute inset-0" />;
};

const FeatureShowcase = () => {
  // Featured item - the main entry point for new users
  const featured = {
    icon: FaCode,
    title: "Interactive Lessons",
    description:
      "Apex, SOQL, & OOP. Bite-sized lessons covering the fundamentals. Each paired with a challenge to solve.",
    link: "/courses",
    tag: "Start Here",
  };

  // Secondary features in logical learning progression
  const features = [
    {
      icon: GiMountainClimbing,
      title: "Practice",
      description: "Sharpen your skills with hands-on Apex challenges",
      link: "/practice/",
    },
    {
      icon: FaTools,
      title: "Real-world Projects",
      description: "Build custom Salesforce apps from scratch using Flow or Apex",
      link: "/projects",
    },
    {
      icon: PiFlowArrowBold,
      title: "Flow Into Apex",
      description: "Convert Flows into Apex to level up your coding skills",
      link: "/practice/flow-into-apex",
    },
    {
      icon: GiTrophy,
      title: "Competitions",
      description: "Compete head-to-head for a spot on the leaderboard",
      link: "/compete",
    },
    {
      icon: GiGingerbreadMan,
      title: "Advent of Salesforce",
      description: "25 days of festive challenges. Live Dec 1st - 25th",
      link: "/advent/2025",
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-zinc-900 via-zinc-900 to-zinc-800 relative overflow-hidden">
      {/* Subtle background accent blobs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-3 text-primary">
            A little something for everyone
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Whether you're just starting out or leveling up your skills, we've got you covered
          </p>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Featured Card - Spans 2 columns on larger screens */}
          <Link to={featured.link} className="block group md:col-span-2 lg:col-span-2 lg:row-span-2">
            <div className="relative h-full min-h-[200px] md:min-h-[280px] p-8 rounded-2xl overflow-hidden
              bg-zinc-800 border border-primary/40 hover:border-primary
              transition-all duration-300 ease-out hover:shadow-2xl hover:shadow-primary/20">
              {/* Decorative accent */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              {featured.tag && (
                <span className="absolute top-4 right-4 px-3 py-1 text-xs font-semibold rounded-full bg-primary text-white shadow-lg">
                  {featured.tag}
                </span>
              )}
              <div className="flex flex-col h-full justify-between relative z-10">
                <div>
                  <div className="inline-flex p-3 rounded-xl bg-primary/20 mb-4 border border-primary/20">
                    <featured.icon className="text-4xl text-primary" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-3 group-hover:text-primary transition-colors">
                    {featured.title}
                  </h3>
                  <p className="text-gray-200 text-base md:text-lg max-w-md">
                    {featured.description}
                  </p>
                </div>
                <div className="mt-6 flex items-center text-primary font-medium group-hover:gap-3 gap-2 transition-all">
                  Get Started <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </Link>

          {/* Regular Feature Cards */}
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ icon: Icon, title, description, link, tag }) => {
  // Different accent colors for variety
  const cardStyles = {
    Practice: "hover:border-emerald-500/50 hover:shadow-emerald-500/10",
    "Real-world Projects": "hover:border-amber-500/50 hover:shadow-amber-500/10",
    "Flow Into Apex": "hover:border-blue-500/50 hover:shadow-blue-500/10",
    Competitions: "hover:border-yellow-500/50 hover:shadow-yellow-500/10",
    "Advent of Salesforce": "hover:border-red-400/50 hover:shadow-red-400/10",
  };

  const iconColors = {
    Practice: "text-emerald-400 group-hover:text-emerald-300",
    "Real-world Projects": "text-amber-400 group-hover:text-amber-300",
    "Flow Into Apex": "text-blue-400 group-hover:text-blue-300",
    Competitions: "text-yellow-400 group-hover:text-yellow-300",
    "Advent of Salesforce": "text-red-400 group-hover:text-red-300",
  };

  return (
    <Link to={link} className="block group">
      <div className={`relative h-full p-5 rounded-xl bg-zinc-800/80 border border-zinc-700/50
        transition-all duration-300 ease-out hover:bg-zinc-800 hover:shadow-xl
        ${cardStyles[title] || "hover:border-primary/50 hover:shadow-primary/10"}`}>
        {tag && (
          <span className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-500 text-white">
            {tag}
          </span>
        )}
        <div className="flex items-start gap-4">
          <div className="shrink-0 p-2.5 rounded-lg bg-zinc-700/70 group-hover:bg-zinc-700 transition-colors">
            <Icon className={`text-xl transition-colors ${iconColors[title] || "text-primary"}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold mb-1 text-gray-100 group-hover:text-white transition-colors">{title}</h3>
            <p className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors">{description}</p>
          </div>
        </div>
      </div>
    </Link>
  );
};

const InteractiveDemo = () => {
  const [code] = React.useState(`public class NameBuilder {
  private String firstName = '';
  private String lastName = '';
  private String fullName = '';

  public void setFirstName(String firstName) {
    this.firstName = firstName;
    updateFullName();
  }

  public void setLastName(String lastName) {
    this.lastName = lastName;
    updateFullName();
  }

  private void updateFullName() {
    this.fullName = this.firstName + ' ' + this.lastName;
  }

  public String getFullName() {
    return this.fullName;
  }
}`);

  const features = [
    {
      title: "Learn and Practice",
      description: "After each lesson, apply what you've learned with built-in challenges.",
      color: "text-emerald-400",
      bgColor: "bg-emerald-400/10",
    },
    {
      title: "Hands-on and Self-Paced",
      description: "Go at a speed that works for you. Camp Apex ensures you grasp each concept before moving on.",
      color: "text-amber-400",
      bgColor: "bg-amber-400/10",
    },
    {
      title: "Real Code, Real Experience",
      description: "Don't just watch - do! Write real code, solve real problems, and gain practical experience.",
      color: "text-blue-400",
      bgColor: "bg-blue-400/10",
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-zinc-800 via-zinc-800 to-zinc-900 relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2"></div>

      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-3">
            Read a little, practice a lot
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Our hands-on approach ensures you're writing code from day one
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-stretch gap-8 lg:gap-12">
          {/* Features list */}
          <div className="lg:w-5/12 flex flex-col justify-center space-y-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-zinc-700/30 border border-zinc-700/50 transition-colors">
                <div className={`shrink-0 w-8 h-8 rounded-lg ${feature.bgColor} flex items-center justify-center`}>
                  <span className={`text-lg font-bold ${feature.color}`}>{index + 1}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1 text-white">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Code editor */}
          <div className="lg:w-7/12">
            <div className="h-[400px] md:h-[450px] rounded-xl overflow-hidden border border-zinc-700 shadow-2xl">
              {/* Editor header */}
              <div className="bg-zinc-900 px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 text-xs ml-2">NameBuilder.cls</span>
                </div>
              </div>
              {/* Editor body */}
              <div className="h-[calc(100%-44px)] bg-[#1e1e1e]">
                <Editor
                  defaultLanguage="apex"
                  theme="vs-dark"
                  value={code}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    readOnly: false,
                    scrollBeyondLastLine: false,
                    padding: { top: 16 },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  return (
    <section className="py-16 md:py-20 bg-gradient-to-b from-zinc-900 to-zinc-900">
      <div className="container mx-auto px-4">
        {/* <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12 text-primary">
          What Campers Are Saying
        </h2> */}
        <div className="w-full">
          <iframe
            id="wall-of-love-2yNE8N"
            src="https://senja.io/p/camp-apex/2yNE8N?hideNavigation=true&embed=true"
            title="Wall of Love"
            frameBorder="0"
            scrolling="no"
            className="w-full"
            style={{ width: "1px", minWidth: "100%" }}
          />
        </div>
      </div>
    </section>
  );
};

const CallToAction = ({ navigate }) => (
  <section className="pt-12 pb-16 md:pt-16 md:pb-20 bg-zinc-900 relative overflow-hidden border-t border-zinc-800">
    {/* Subtle background accent */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>

    <div className="container mx-auto px-4 max-w-3xl relative z-10">
      <div className="text-center">
        <p className="text-gray-300 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed">
          Transform your skills with hands-on, interactive learning.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            className="group w-full sm:w-auto px-8 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl
              transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-0.5"
            onClick={() => navigate("/auth")}
          >
            <span className="flex items-center justify-center gap-2">
              Start Your Journey
              <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
          <button
            className="w-full sm:w-auto px-8 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded-xl
              border border-zinc-700 hover:border-zinc-600 transition-all duration-300"
            onClick={() => navigate("/courses")}
          >
            Browse Courses
          </button>
        </div>

        <p className="mt-8 text-zinc-400 text-sm max-w-lg mx-auto leading-relaxed">
          This platform is privately funded and provided free of charge by {" "}
          <a
            href="https://www.linkedin.com/in/saman-attar/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Saman Attar
          </a>
          {" "} who believes everyone, regardless of financial position, should be able to learn how to code.
        </p>
      </div>
    </div>
  </section>
);

export default HomePage;
